/**
 * rb-ai.js — the page's side of the resume assistant.
 *
 * POST /{locale}/resume-builder/ai/{task} starts a job; the page polls
 * /ai-job/{token} until the answer is there (ResumeAiHandler, ResumeAiJob).
 * Why polling and not one request: on the RU half the measured model answers
 * in 10–60 s (once 91 s) while the edge closes a request at 120 s — the
 * reasoning that keeps it honest is also what takes the time (config/resume-ai.php).
 * So the wait is shown honestly: "usually about N s", a bar that fills toward
 * that time and slows near the end, never a fake countdown.
 *
 * Every AI call is the person's explicit press; nothing here runs by itself.
 *
 * globalThis.RbAi
 */
(function () {
    'use strict';

    var cfg = null, T = null, track = function () {}, chip = null;
    var quota = null;   /* {ai:{cap,left}, import:{cap,left}, premium, live} */

    function init(o) {
        cfg = o.cfg || {};
        T = o.t;
        track = o.track || track;
        chip = o.chip || null;
        if (live()) refreshQuota();
    }
    function live() { return !!(cfg && cfg.ai && cfg.ai.live); }

    function csrf() {
        var m = document.querySelector('meta[name="csrf-token"]');
        return m ? m.getAttribute('content') : '';
    }

    function refreshQuota() {
        if (!live()) return Promise.resolve(null);
        return fetch(cfg.ai.quota, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (j) { if (j && j.success) { quota = j; paintChip(); } return quota; })
            .catch(function () { return null; });
    }
    function setLeft(bucket, left, cap) {
        if (!quota) quota = { ai: {}, import: {} };
        if (!quota[bucket]) quota[bucket] = {};
        if (typeof left === 'number') quota[bucket].left = left;
        if (typeof cap === 'number') quota[bucket].cap = cap;
        paintChip();
    }
    function left(bucket) { return quota && quota[bucket] ? quota[bucket].left : null; }

    function paintChip() {
        if (!chip || !quota || !quota.ai) return;
        var q = quota.ai;
        if (typeof q.cap !== 'number') return;
        // Без своих действий (без Премиума) счётчику нечего показывать: кнопки
        // работают, а цену называет ответ сервера (30.10.2026).
        if (q.cap <= 0) { chip.hidden = true; return; }
        chip.hidden = false;
        chip.innerHTML = '✨ ' + esc(T('ai_chip', { left: '\u0001', cap: q.cap })).replace('\u0001', '<b>' + Math.max(0, q.left | 0) + '</b>');
        chip.title = T('ai_chip_hint', { cap: q.cap });   // свой месяц Премиума; сверх — за токены
    }

    function esc(s) {
        return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
    }

    /** One line under every AI button: where the text goes, as the code does it. */
    function disclosure() {
        if (!live()) return '';
        var key = cfg.ai.provider === 'yandex' ? 'ai_where_yandex' : 'ai_where_anthropic';
        return esc(T(key)) + ' <a href="' + esc(cfg.ai.providers) + '" target="_blank" rel="noopener">' + esc(T('ai_where_more')) + '</a>';
    }

    /**
     * Run a task. opts.onWait(info) is called while waiting:
     *   {eta, waited, frac}. opts.signal — AbortSignal (the person's "Stop").
     * Resolves with the result object; rejects with {kind, message, premium}.
     */
    function run(task, body, opts) {
        opts = opts || {};
        if (!live()) return Promise.reject({ kind: 'off', message: T('ai_err_off') });
        var t0 = performance.now();
        var bucket = task === 'parse' ? 'import' : 'ai';
        var url = cfg.ai.run.replace('/TASK', '/' + task);
        track('ai_start', { task: task });
        // Согласие на действие за токены (public/js/lane-pay.js): свой месяц Премиум тратит первым.
        if (globalThis.__lanePay && globalThis.__lanePay.agreed('resume-builder') && body && typeof body === 'object') {
            body.pay = 'tokens';
        }

        function fail(j, status) {
            var e = { kind: (j && j.kind) || (status === 429 ? 'limit' : 'broken'), message: (j && j.error) || T('ai_err_broken'), premium: !!(j && j.premium), status: status, data: j };
            if (j && j.error === 'insufficient_tokens') { e.kind = 'tokens'; e.message = j.msg || e.message; }
            if (e.kind === 'limit') setLeft(bucket, 0);
            track('ai_fail', { task: task, kind: e.kind, ms: Math.round(performance.now() - t0) });
            return Promise.reject(e);
        }

        return fetch(url, {
            method: 'POST', credentials: 'same-origin', signal: opts.signal,
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': csrf(), 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify(body)
        }).then(function (r) {
            return r.json().catch(function () { return null; }).then(function (j) {
                /* anything but an explicit success is a failure — a server error page
                   arrives as JSON without success and must not be polled as a job */
                if (!j || j.success !== true) return fail(j, r.status);
                if (j.status === 'done') return done(j);
                if (!/^[a-f0-9]{32}$/.test(String(j.job || ''))) return fail(null, r.status);
                return poll(j.job, j.eta || 20);
            });
        }, function (err) {
            if (opts.signal && opts.signal.aborted) return Promise.reject({ kind: 'cancel' });
            return fail({ kind: 'busy', error: T('ai_err_net') }, 0);
        });

        function done(j) {
            if (typeof j.left === 'number') setLeft(bucket, j.left, j.cap);
            if (j.paid_tokens > 0 && j.paid_text && globalThis.__toast) globalThis.__toast(j.paid_text, 'info');
            track('ai_done', { task: task, ms: Math.round(performance.now() - t0) });
            return j.result;
        }

        function poll(token, eta) {
            var started = performance.now();
            var jobUrl = cfg.ai.job.replace(/0{32}/, token);
            return new Promise(function (resolve, reject) {
                var tries = 0;
                var timer = null;
                var tick = setInterval(function () {
                    var waited = (performance.now() - started) / 1000;
                    /* fills toward the usual time, then creeps: never claims "done" */
                    var frac = waited < eta ? 0.85 * waited / eta : 0.85 + 0.14 * (1 - Math.exp(-(waited - eta) / eta));
                    if (opts.onWait) opts.onWait({ eta: eta, waited: waited, frac: frac });
                }, 500);
                if (opts.signal) opts.signal.addEventListener('abort', function () {
                    clearInterval(tick); clearTimeout(timer);
                    track('ai_cancel', { task: task });
                    reject({ kind: 'cancel' });
                });
                function next() {
                    if (opts.signal && opts.signal.aborted) return;
                    tries++;
                    if ((performance.now() - started) / 1000 > 170) {
                        clearInterval(tick);
                        return fail({ kind: 'busy', error: T('ai_err_busy') }, 0).catch(reject);
                    }
                    fetch(jobUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
                        .then(function (r) { return r.json().catch(function () { return null; }).then(function (j) { return { r: r, j: j }; }); })
                        .then(function (x) {
                            if (opts.signal && opts.signal.aborted) return;
                            var j = x.j;
                            if (j && j.success && j.status === 'done') { clearInterval(tick); resolve(done(j)); return; }
                            if (!j || j.success !== true) { clearInterval(tick); fail(j, x.r.status).catch(reject); return; }
                            timer = setTimeout(next, tries < 6 ? 1200 : 2500);
                        }, function () { timer = setTimeout(next, 3000); });
                }
                timer = setTimeout(next, 900);
            });
        }
    }

    /* ---- small shared UI ------------------------------------------------ */

    /** A waiting block inside `host`. Returns {set(info), remove()}. */
    function waitBlock(host, label) {
        var el = document.createElement('div');
        el.className = 'rbx-sugg';
        el.setAttribute('role', 'status');
        el.innerHTML = '<div class="rbx-wait"><i aria-hidden="true"></i><span class="rbx-wait-t"></span>' +
            '<button type="button" class="tc-btn ghost sm" data-ai-stop style="margin-inline-start:auto">' + esc(T('ai_stop')) + '</button></div>' +
            '<div class="rbx-wait-bar"><span></span></div>';
        host.appendChild(el);
        var t = el.querySelector('.rbx-wait-t'), bar = el.querySelector('.rbx-wait-bar span');
        t.textContent = label;
        return {
            el: el,
            stopBtn: el.querySelector('[data-ai-stop]'),
            set: function (info) {
                bar.style.width = Math.round(info.frac * 100) + '%';
                t.textContent = label + ' · ' + (info.waited < info.eta ? T('ai_wait_usual', { s: Math.round(info.eta) }) : T('ai_wait_long'));
            },
            remove: function () { if (el.parentNode) el.parentNode.removeChild(el); }
        };
    }

    /** The answer to a refusal, in place: message + the way out. retry — a priced refusal's «run for tokens». */
    function errorBlock(host, e, retry) {
        if (e && e.data && globalThis.__lanePay) {
            var pay = document.createElement('div');
            if (globalThis.__lanePay.show(pay, e.data, { slug: 'resume-builder', run: retry || function () {} })) {
                host.appendChild(pay);
                return pay;
            }
        }
        var el = document.createElement('div');
        el.className = 'rbx-sugg';
        el.setAttribute('role', 'alert');
        var act = '';
        if (e.kind === 'limit' && e.premium) act = '<a class="tc-btn sm" href="' + esc(cfg.premium) + '">' + esc(T('ai_get_premium')) + '</a>';
        if (e.kind === 'verify') act = '<a class="tc-btn sm ghost" href="' + esc(cfg.profile || '/') + '">' + esc(T('ai_verify_cta')) + '</a>';
        el.innerHTML = '<p class="rbx-sugg-text">' + esc(e.message || T('ai_err_broken')) + '</p>' + (act ? '<div class="rbx-sugg-act">' + act + '</div>' : '');
        host.appendChild(el);
        return el;
    }

    /** Escape text and highlight "[…]" placeholders as things to fill in. */
    function marked(s) {
        return esc(s).replace(/\[([^\]\n]{1,80})\]/g, '<mark class="rbx-mark">[$1]</mark>');
    }

    globalThis.RbAi = {
        init: init, live: live, run: run, refreshQuota: refreshQuota, left: left,
        disclosure: disclosure, waitBlock: waitBlock, errorBlock: errorBlock, marked: marked, esc: esc
    };
})();
